import { GameState, GameStateImpl } from '../../domain/entities/GameState';
import { Memory } from '../../domain/entities/Memory';
import { GameConfig, Question, QuizContent, MemoryContent, TransitionContent } from '../../domain/entities/GameConfig';
import { gameContent, initializeGameContent } from '../../config/gameContent';

export interface GameService {
    initializeGame(): Promise<void>;
    getCurrentState(): GameState;
    getCurrentQuestion(): Question | null;
    getCurrentContent(): QuizContent | MemoryContent | TransitionContent | null;
    submitAnswer(levelId: string, answer: string): Promise<boolean>;
    progressToNextLevel(): Promise<void>;
    loadMemories(): Promise<Memory[]>;
}

export class GameServiceImpl implements GameService {
    private gameState: GameState;
    private gameConfig: GameConfig;
    private currentQuestion: Question | null = null;
    private readonly STORAGE_KEY = 'valentines_game_state';

    constructor() {
        this.gameState = new GameStateImpl();
        this.gameConfig = gameContent;
    }

    async initializeGame(): Promise<void> {
        // Load game config first
        this.gameConfig = await initializeGameContent();
        
        // Only initialize new state if there's no saved state or it's a manual refresh
        const savedState = this.loadSavedState();
        if (savedState && !window.performance.navigation.type) {
            this.gameState = savedState;
        } else {
            // Initialize with correct section and level counts
            this.gameState = new GameStateImpl(
                1, // currentSection
                1, // currentLevel
                [], // completedLevels
                new Date(), // lastUpdated
                this.gameConfig.sections.length, // maxSections
                Math.max(...this.gameConfig.sections.map(s => s.levels.length)) // maxLevels
            );
        }
        
        this.loadCurrentQuestion();
        await this.loadMemories();
    }

private loadSavedState(): GameState | null {
    try {
        const savedStateJson = localStorage.getItem(this.STORAGE_KEY);
        if (!savedStateJson) return null;
        
        const savedData = JSON.parse(savedStateJson);
        
        // Create a proper GameStateImpl instance with the saved data
        return new GameStateImpl(
            savedData.currentSection,
            savedData.currentLevel,
            savedData._completedLevels || [], // Use the private field that was saved
            new Date(savedData.lastUpdated),
            savedData.maxSections,
            savedData.maxLevels
        );
    } catch (e) {
        console.error("Error loading saved state:", e);
        return null;
    }
}

    private saveState(): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.gameState));
        } catch (error) {
            console.error('Error saving state:', error);
        }
    }

    getCurrentState(): GameState {
        return this.gameState;
    }

    getCurrentQuestion(): Question | null {
        return this.currentQuestion;
    }

    getCurrentContent(): QuizContent | MemoryContent | TransitionContent | null {
        const currentSection = this.gameConfig.sections[this.gameState.currentSection - 1];
        if (!currentSection) return null;

        const currentLevel = currentSection.levels[this.gameState.currentLevel - 1];
        if (!currentLevel) return null;

        return currentLevel.content;
    }

    private loadCurrentQuestion(): void {
        const content = this.getCurrentContent();
        if (!content || content.type !== 'QUIZ') {
            this.currentQuestion = null;
            return;
        }

        
        // For quiz content, get the first unanswered question
        this.currentQuestion = content.questions.find(
            q => !this.gameState.completedLevels.includes(q.id)
        ) || null;
    }

    async submitAnswer(levelId: string, answer: string): Promise<boolean> {
        const isCorrect = await this.validateAnswer(levelId, answer);
        if (isCorrect) {
            // Create new state with completed level
            const newState = (this.gameState as GameStateImpl)
                .addCompletedLevel(levelId);
            
            // Update the state
            this.gameState = newState;
            this.saveState();
        }
        return isCorrect;
    }

    async progressToNextLevel(): Promise<void> {
        const currentSection = this.gameConfig.sections[this.gameState.currentSection - 1];
        if (!currentSection) return;

        const currentLevel = currentSection.levels[this.gameState.currentLevel - 1];
        if (!currentLevel) return;

        const content = currentLevel.content;

        switch (content.type) {
            case 'MEMORY':
            case 'TRANSITION':
                // For memory and transition content, check if we need to move to the next section
                if (this.gameState.currentLevel >= currentSection.levels.length) {
                    // Move to next section if we're at the last level
                    const newState = (this.gameState as GameStateImpl).progressToNextSection();
                    this.gameState = newState;
                } else {
                    // Otherwise, move to next level in current section
                    const newState = (this.gameState as GameStateImpl).progressToNextLevel();
                    this.gameState = newState;
                }
                this.saveState();
                this.loadCurrentQuestion();
                console.log('Moving to next:', {
                    section: this.gameState.currentSection,
                    level: this.gameState.currentLevel
                });
                return;

            case 'QUIZ':
                // Check if all questions in current level are completed
                const allQuestionsCompleted = content.questions.every(
                    q => this.gameState.completedLevels.includes(q.id)
                );

                if (allQuestionsCompleted) {
                    // Check if we need to move to the next section
                    if (this.gameState.currentLevel >= currentSection.levels.length) {
                        // Move to next section
                        const newState = (this.gameState as GameStateImpl).progressToNextSection();
                        this.gameState = newState;
                    } else {
                        // Move to next level in current section
                        const newState = (this.gameState as GameStateImpl).progressToNextLevel();
                        this.gameState = newState;
                    }
                    
                    // Save state and load next question
                    this.saveState();
                    this.loadCurrentQuestion();
                    
                    console.log('Progressing to:', {
                        section: this.gameState.currentSection,
                        level: this.gameState.currentLevel,
                        completedLevels: this.gameState.completedLevels
                    });
                } else {
                    // If we haven't completed all questions, load the next unanswered question
                    this.loadCurrentQuestion();
                    
                    console.log('Cannot progress:', {
                        allQuestionsCompleted,
                        completedLevels: this.gameState.completedLevels,
                        levelQuestions: content.questions.map(q => q.id)
                    });
                }
                break;
        }
    }

    async loadMemories(): Promise<Memory[]> {
        return this.gameConfig.memories;
    }

    private async validateAnswer(_levelId: string, answer: string): Promise<boolean> {
        if (!this.currentQuestion) return false;

        switch (this.currentQuestion.type) {
            case 'MULTIPLE_CHOICE':
                return this.validateMultipleChoice(answer);
            case 'TEXT':
            case 'FILL_BLANK':
                return this.validateTextInput(answer);
            case 'DRAG_DROP':
                return this.validateDragDrop(answer);
            case 'UNSCRAMBLE':
                return this.validateUnscramble(answer);
            default:
                return false;
        }
    }

    private validateMultipleChoice(answer: string): boolean {
        if (!this.currentQuestion) return false;
        if (Array.isArray(this.currentQuestion.correctAnswer)) {
            return this.currentQuestion.correctAnswer.includes(answer);
        }
        return this.currentQuestion.correctAnswer === answer;
    }

    private validateTextInput(answer: string): boolean {
        if (!this.currentQuestion) return false;

        const config = this.currentQuestion.textInputConfig;
        const userAnswer = config?.caseSensitive ? answer : answer.toLowerCase();
        const correctAnswer = config?.caseSensitive ? 
            (Array.isArray(this.currentQuestion.correctAnswer) 
                ? this.currentQuestion.correctAnswer[0] 
                : this.currentQuestion.correctAnswer) :
            (Array.isArray(this.currentQuestion.correctAnswer)
                ? this.currentQuestion.correctAnswer[0].toLowerCase()
                : this.currentQuestion.correctAnswer.toLowerCase());

        // Check exact match
        if (userAnswer === correctAnswer) return true;

        // Check allowed variations
        if (config?.allowedVariations) {
            const variations = config.caseSensitive ? 
                config.allowedVariations :
                config.allowedVariations.map(v => v.toLowerCase());
            if (variations.includes(userAnswer)) return true;
        }

        // Check fuzzy match if enabled
        if (config?.fuzzyMatch) {
            return this.fuzzyMatch(userAnswer, correctAnswer);
        }

        return false;
    }

    private validateDragDrop(answer: string): boolean {
        if (!this.currentQuestion || !this.currentQuestion.dragDropConfig) return false;

        try {
            const userOrder = JSON.parse(answer);
            const correctOrder = Array.isArray(this.currentQuestion.correctAnswer) 
                ? this.currentQuestion.correctAnswer 
                : [this.currentQuestion.correctAnswer];

            if (this.currentQuestion.dragDropConfig.matchType === 'EXACT') {
                return JSON.stringify(userOrder) === JSON.stringify(correctOrder);
            } else {
                // For ORDER_ONLY, just check if the items are in the same order
                return userOrder.every((item: string, index: number) => 
                    item.toLowerCase() === correctOrder[index].toLowerCase()
                );
            }
        } catch {
            return false;
        }
    }

    private validateUnscramble(answer: string): boolean {
        if (!this.currentQuestion) return false;

        const userAnswer = answer.toLowerCase().replace(/\s+/g, ' ').trim();
        const correctAnswer = (Array.isArray(this.currentQuestion.correctAnswer) 
            ? this.currentQuestion.correctAnswer.join(' ')
            : this.currentQuestion.correctAnswer)
            .toLowerCase().replace(/\s+/g, ' ').trim();

        return userAnswer === correctAnswer;
    }

    private fuzzyMatch(str1: string, str2: string): boolean {
        // Simple Levenshtein distance implementation
        const m = str1.length;
        const n = str2.length;
        const dp: number[][] = Array(m + 1).fill(0).map(() => Array(n + 1).fill(0));

        for (let i = 0; i <= m; i++) dp[i][0] = i;
        for (let j = 0; j <= n; j++) dp[0][j] = j;

        for (let i = 1; i <= m; i++) {
            for (let j = 1; j <= n; j++) {
                if (str1[i - 1] === str2[j - 1]) {
                    dp[i][j] = dp[i - 1][j - 1];
                } else {
                    dp[i][j] = Math.min(
                        dp[i - 1][j - 1] + 1,
                        dp[i - 1][j] + 1,
                        dp[i][j - 1] + 1
                    );
                }
            }
        }

        // Allow for some fuzzy matching (e.g., allow 2 character differences for every 10 characters)
        const maxAllowedDistance = Math.floor(Math.max(m, n) / 5);
        return dp[m][n] <= maxAllowedDistance;
    }
} 